import React, { useMemo } from "react";
import { ColDef } from "ag-grid-community";
import { euroFormatterTwoDecimals, paramsEuroFormatter, redOrGreenText } from "@/utils/formatters";
import Grid, { GridDropdownFilter } from "../../lib/organisms/Grid";
import { CustomCellRendererProps } from "ag-grid-react";
import { PeriodName } from "@/models";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { getPeriodStartAndEndDates } from "@/utils/periods";
import { Apartment, PayoutStat } from "@/models/responses";
import { useGetPayoutsQuery } from "@/store/booking-management-api";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";

interface PerformancePerUnitRow {
    apartment: string;
    netIncome: number;
    managementFee: number;
    checkInFee: number;
    ownerGroupFee: number;
    periodDeposit: number;
    cumulativeDeposit: number;
    kerosNetIncome: number;
    kerosNetIncomePercentage: number;
    kerosNetIncomePreFinancing: number;
    kerosNetIncomePreFinancingPercentage: number;
}

function getCheckInFee(payout: PayoutStat, apartment: Apartment) {
    return payout.netIncome * apartment.checkInFeeRate;
}

const PerformancePerUnitPage: React.FC = () => {
    const { yearOptions, periodOptions, selectedPeriod, selectedYear, apartmentDetailsMap, ownerGroups, groupOptions, selectedGroup } = useAppData();

    // Apartment-level filters (bedrooms, municipality, owner-group, tags) now
    // live in the shared `apartmentFilters` Redux slice and are rendered by
    // `<ApartmentFilterBar />` below. The Group control remains a page-local,
    // userSettings-backed filter — it narrows along the `groupId` dimension,
    // which is distinct from `ownerGroupId` and outside the Tier 1 shared
    // filter set.
    const { predicates } = useApartmentFilters();

    const filters = useMemo(() => {
        const preds = [...predicates];
        if (selectedGroup !== 0) {
            preds.push((a: Apartment) => a.groupId === selectedGroup);
        }
        return preds;
    }, [predicates, selectedGroup]);

    const { setSelectedYear, setSelectedPeriod, setSelectedGroup } = useUserSettingsDispatch();

    const gridName = "Host Performance per Unit";

    const [startDate, endDate] = useMemo(() => {
        return getPeriodStartAndEndDates(selectedYear, selectedPeriod);
    }, [selectedYear, selectedPeriod]);

    const monthsBetweenStartAndEndDate = useMemo(() => {
        return Math.abs(startDate.getMonth() - endDate.getMonth()) + 1;
    }, [startDate, endDate]);

    const { data: payouts, isLoading: isPayoutsLoading, error: hasPayoutsErrored } = useGetPayoutsQuery({ startDate, endDate });

    const payoutRows: PerformancePerUnitRow[] = useMemo(() => {
        if (payouts === undefined) {
            return [];
        }

        return payouts
            .filter((s) => {
                const apartment = apartmentDetailsMap.get(s.apartmentId);
                if (!apartment) {
                    return false;
                }
                return filters.every((filter) => filter(apartment));
            })
            .map((payout: PayoutStat) => {
                const apartmentDetails = apartmentDetailsMap.get(payout.apartmentId);
                if (!apartmentDetails) {
                    return {
                        apartment: "",
                        netIncome: 0,
                        managementFee: 0,
                        checkInFee: 0,
                        ownerGroupFee: 0,
                        periodDeposit: 0,
                        cumulativeDeposit: 0,
                        kerosNetIncome: 0,
                        kerosNetIncomePercentage: 0,
                        kerosNetIncomePreFinancing: 0,
                        kerosNetIncomePreFinancingPercentage: 0,
                    };
                }
                const ownerGroup = ownerGroups.find((og) => og.id === apartmentDetails?.ownerGroupId);
                const ownerGroupFee = ownerGroup ? monthsBetweenStartAndEndDate * ownerGroup.monthlyFee : 0;
                const checkInFee = getCheckInFee(payout, apartmentDetails);
                const kerosNetIncomePreFinancing = payout.managementFee - ownerGroupFee - checkInFee;
                const kerosNetIncome = payout.deposit < 0 ? kerosNetIncomePreFinancing + payout.deposit : kerosNetIncomePreFinancing;
                return {
                    apartment: apartmentDetails.name,
                    netIncome: payout.netIncome,
                    managementFee: payout.managementFee,
                    checkInFee: checkInFee,
                    ownerGroupFee: ownerGroupFee,
                    periodDeposit: payout.deposit,
                    cumulativeDeposit: payout.cumulativeDeposit,
                    kerosNetIncome: kerosNetIncome,
                    kerosNetIncomePercentage: Math.round((kerosNetIncome / payout.netIncome) * 100),
                    kerosNetIncomePreFinancing: kerosNetIncomePreFinancing,
                    kerosNetIncomePreFinancingPercentage: Math.round((kerosNetIncomePreFinancing / payout.netIncome) * 100),
                };
            });
    }, [payouts, apartmentDetailsMap, filters, ownerGroups, monthsBetweenStartAndEndDate]);

    const netIncomeColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Net Income",
        field: "netIncome",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 100,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.netIncome !== undefined) {
                return Math.round(value.data.netIncome);
            }
        },
    };
    const managementFeeColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Management Fee",
        field: "managementFee",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 120,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.managementFee !== undefined) {
                return Math.round(value.data.managementFee);
            }
        },
    };
    const checkInColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Check In Fee",
        field: "checkInFee",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 90,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.checkInFee !== undefined) {
                return Math.round(value.data.checkInFee);
            }
        },
    };
    const ownerGroupFeeColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Owner Group Fee",
        field: "ownerGroupFee",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 110,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.ownerGroupFee !== undefined) {
                return Math.round(value.data.ownerGroupFee);
            }
        },
    };
    const depositColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Period Deposit",
        field: "periodDeposit",
        valueFormatter: paramsEuroFormatter,
        cellClassRules: redOrGreenText,
        cellClass: "currencyFormat",
        cellDataType: "number",
        cellRenderer: (params: CustomCellRendererProps) => {
            const color = params.value > 0 ? "green" : "red";
            return (
                <div
                    style={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: color === "green" ? "#dcfce7" : "#fee2e2",
                    }}
                    className=""
                >
                    <span className={`w-full p-2 text-right text-${color}-600`}>{paramsEuroFormatter(params)}</span>
                </div>
            );
        },
        width: 120,
        filter: "agNumberColumnFilter",
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.periodDeposit !== undefined) {
                return Math.round(value.data.periodDeposit);
            }
        },
    };
    const cumulativeDepositColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Cumulative Deposit",
        field: "cumulativeDeposit",
        valueFormatter: paramsEuroFormatter,
        cellClassRules: redOrGreenText,
        cellClass: "currencyFormat",
        cellDataType: "number",
        cellRenderer: (params: CustomCellRendererProps) => {
            const color = params.value > 0 ? "green" : "red";
            return (
                <div
                    style={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: color === "green" ? "#dcfce7" : "#fee2e2",
                    }}
                    className=""
                >
                    <span className={`w-full p-2 text-right text-${color}-600`}>{paramsEuroFormatter(params)}</span>
                </div>
            );
        },
        width: 120,
        filter: "agNumberColumnFilter",
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.cumulativeDeposit !== undefined) {
                return Math.round(value.data.cumulativeDeposit);
            }
        },
    };

    const kerosNetIncomeColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Keros Net Income",
        field: "kerosNetIncome",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.kerosNetIncome !== undefined) {
                return Math.round(value.data.kerosNetIncome);
            }
        },
    };
    const kerosNetIncomePercentageColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Keros Net Income  %",
        field: "kerosNetIncomePercentage",
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.kerosNetIncome !== undefined) {
                return Math.round((value.data.kerosNetIncome / value.data.netIncome) * 100);
            }
        },
        valueFormatter: (params) => {
            return params.value + "%";
        },
    };
    const kerosNetIncomePreFinancingColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Keros Net Income Pre Financing",
        field: "kerosNetIncomePreFinancing",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.kerosNetIncomePreFinancing !== undefined) {
                return Math.round(value.data.kerosNetIncomePreFinancing);
            }
        },
    };
    const kerosNetIncomePreFinancingPercentageColumn: ColDef<PerformancePerUnitRow> = {
        headerName: "Keros Net Income Pre Financing %",
        cellClass: "currencyFormat",
        cellDataType: "number",
        field: "kerosNetIncomePreFinancingPercentage",
        filter: "agNumberColumnFilter",
        width: 130,
        filterValueGetter: (value) => {
            if (value.data !== undefined && value.data.kerosNetIncomePreFinancingPercentage !== undefined) {
                return Math.round((value.data.kerosNetIncomePreFinancingPercentage / value.data.netIncome) * 100);
            }
        },
        valueFormatter: (params) => {
            return params.value + "%";
        },
    };

    const columnDefinitions: ColDef[] = [
        {
            headerName: "Apartment",
            field: "apartment",
            cellDataType: "text",
            width: 270,
            filter: "agSetColumnFilter",
        },
        netIncomeColumn,
        managementFeeColumn,
        checkInColumn,
        ownerGroupFeeColumn,
        depositColumn,
        cumulativeDepositColumn,
        kerosNetIncomeColumn,
        kerosNetIncomePercentageColumn,
        kerosNetIncomePreFinancingColumn,
        kerosNetIncomePreFinancingPercentageColumn,
    ];

    const gridDropdownFilters: GridDropdownFilter[] = [
        {
            label: "Group",
            options: groupOptions,
            selectedOption: selectedGroup.toString(),
            onSelectionChanged: (group) => setSelectedGroup(Number(group)),
        },
        {
            label: "Period",
            options: periodOptions,
            selectedOption: selectedPeriod,
            onSelectionChanged: (period) => setSelectedPeriod(period as PeriodName),
        },
        {
            label: "Year",
            options: yearOptions,
            selectedOption: selectedYear.toString(),
            onSelectionChanged: (year) => setSelectedYear(Number(year)),
        },
    ];

    if (isPayoutsLoading || payouts === undefined) {
        return <div data-testid="performance-page">Loading...</div>;
    }

    if (hasPayoutsErrored) {
        return <div data-testid="performance-page">Error</div>;
    }

    return (
        <div className="flex flex-col space-y-2" data-testid="performance-page">
            <ApartmentFilterBar />
            <Grid<PerformancePerUnitRow>
                columnDefs={columnDefinitions}
                gridName={gridName}
                dropdownFilters={gridDropdownFilters}
                initialRowData={payoutRows}
                getRowId={(params) => params.data.apartment}
                columnsToAggregate={[
                    { colDef: netIncomeColumn, formatter: euroFormatterTwoDecimals },
                    { colDef: managementFeeColumn, formatter: euroFormatterTwoDecimals },
                    { colDef: checkInColumn, formatter: euroFormatterTwoDecimals },
                    { colDef: ownerGroupFeeColumn, formatter: euroFormatterTwoDecimals },
                    { colDef: depositColumn, formatter: euroFormatterTwoDecimals },
                    {
                        colDef: cumulativeDepositColumn,
                        formatter: euroFormatterTwoDecimals,
                    },
                    {
                        colDef: kerosNetIncomePreFinancingColumn,
                        formatter: euroFormatterTwoDecimals,
                    },
                    {
                        colDef: kerosNetIncomePreFinancingPercentageColumn,
                        aggregateFunction: "avg",
                        formatter: (x) => {
                            return x;
                        },
                    },
                    { colDef: kerosNetIncomeColumn, formatter: euroFormatterTwoDecimals },
                    {
                        colDef: kerosNetIncomePercentageColumn,
                        aggregateFunction: "avg",
                        formatter: (x) => {
                            return x;
                        },
                    },
                ]}
            />
        </div>
    );
};

export default PerformancePerUnitPage;
